/**
 * Mi Boleta — qué muestra la pantalla `/login`: ingreso o registro.
 *
 * 🔴 **Por qué esto vive en la URL y no en el estado del componente.** Antes el
 * modo se calculaba UNA sola vez, al montar (`useState(inicial)`). Como ingreso
 * y registro son **la misma ruta**, ir de uno al otro **no vuelve a montar** el
 * componente: React Router cambia la dirección, el inicializador no corre de
 * nuevo, y la pantalla se queda en el modo viejo.
 *
 * El síntoma exacto: estando en "Ingresar", tocar "Crear cuenta" en la barra
 * cambiaba la URL **pero no el formulario** — y al revés. Desde otra sección
 * funcionaba, porque ahí sí había un montaje nuevo. Un defecto que sólo aparece
 * cuando ya estás en la pantalla.
 *
 * Derivándolo de la dirección, **la URL es la única fuente**: no hay dos copias
 * del modo que puedan quedar desincronizadas. De paso, el botón "atrás" y un
 * enlace compartido llevan a la pantalla correcta.
 */

export type LoginMode = 'sign_in' | 'sign_up';

/** Valor de `?modo=` que abre el registro. */
export const SIGN_UP_PARAM = 'registro';

/** Nombre del parámetro. En castellano, como el resto de las rutas de la tienda. */
export const MODE_PARAM = 'modo';

/**
 * Lee el modo de la dirección. Cualquier valor que no sea `registro` —incluido
 * ninguno— es ingreso: el modo por defecto es el que no crea nada.
 */
export function loginModeFromParams(params: URLSearchParams): LoginMode {
  return params.get(MODE_PARAM) === SIGN_UP_PARAM ? 'sign_up' : 'sign_in';
}

/**
 * Los parámetros que corresponden a un modo, **conservando el resto**.
 *
 * Conservar importa: `?from=` trae a dónde volver después de entrar. Si al
 * alternar entre ingreso y registro se perdiera, quien iba a pagar terminaría
 * en el inicio en vez de en su compra.
 */
export function paramsForLoginMode(current: URLSearchParams, mode: LoginMode): URLSearchParams {
  const next = new URLSearchParams(current);
  if (mode === 'sign_up') next.set(MODE_PARAM, SIGN_UP_PARAM);
  else next.delete(MODE_PARAM);
  return next;
}
